import { createContext, useContext, useState, useEffect } from "react";

const LOCAL_MODE = false; // false = use real backend

const TEST_USER = {
  name: "Test User",
  email: "test@example.com",
  password: "test123",
};

const ADMIN_EMAIL = "sharmadeepika85.ds@gmail.com";
const ADMIN_PASSWORD = "nitin21212";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (LOCAL_MODE) {
      const saved = localStorage.getItem("pdfstudio_local_user");
      if (saved) setUser(JSON.parse(saved));
    } else {
      const saved = localStorage.getItem("pdfstudio_user");
      if (saved) setUser(JSON.parse(saved));
    }
  }, []);

  // ✅ SAFE JSON PARSER
  const parseJSON = async (res) => {
    const text = await res.text();
    console.log("AUTH RAW RESPONSE:", text);

    try {
      return text ? JSON.parse(text) : {};
    } catch {
      throw new Error("Invalid JSON from server");
    }
  };

  // ─────────────────────────────
  // 🔐 LOGIN
  // ─────────────────────────────
  const login = async (email, password) => {
    if (LOCAL_MODE) {
      if (email === TEST_USER.email && password === TEST_USER.password) {
        setUser(TEST_USER);
        localStorage.setItem("pdfstudio_local_user", JSON.stringify(TEST_USER));
        return { user: TEST_USER };
      }
      throw new Error("Invalid credentials");
    }

    setLoading(true);
    try {
      const res = await fetch("/api/login.php", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ email, password }),
      });

      const data = await parseJSON(res);

      if (!res.ok || data.error) {
        throw new Error(data.error || "Login failed");
      }

      const rawUser = data.user || {};
      const isAdminLogin =
        email?.toLowerCase() === ADMIN_EMAIL && password === ADMIN_PASSWORD;

      const enrichedUser = {
        ...rawUser,
        // Normalize DB field full_name → name so all components use user.name
        name: rawUser.full_name || rawUser.name || email,
        role: isAdminLogin ? "admin" : rawUser.role || "user",
        isAdmin: isAdminLogin || rawUser.role === "admin",
        // Restore plan to 'free' if pro plan has expired
        plan:
          rawUser.plan === "pro" &&
          rawUser.pro_end_date &&
          new Date(rawUser.pro_end_date) < new Date()
            ? "free"
            : rawUser.plan || "free",
      };

      setUser(enrichedUser);
      localStorage.setItem("pdfstudio_user", JSON.stringify(enrichedUser));
      syncToRegistry(enrichedUser);

      return { ...data, user: enrichedUser };
    } finally {
      setLoading(false);
    }
  };

  // ─────────────────────────────
  // 📝 SIGNUP (FIXED HERE)
  // ─────────────────────────────
  const signup = async (name, email, password) => {
    if (LOCAL_MODE) {
      if (email === TEST_USER.email) throw new Error("User already exists");
      throw new Error("Only test@example.com is allowed in local mode");
    }

    setLoading(true);
    try {
      const res = await fetch("/api/register.php", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          name,
          email,
          password,
        }),
      });

      const data = await parseJSON(res);

      if (!res.ok || data.error) {
        throw new Error(data.error || "Signup failed");
      }

      // ✅ Auto login after signup
      return await login(email, password);
    } finally {
      setLoading(false);
    }
  };

  // ─────────────────────────────
  // 🚪 LOGOUT
  // ─────────────────────────────
  const logout = () => {
    if (LOCAL_MODE) {
      localStorage.removeItem("pdfstudio_local_user");
    } else {
      localStorage.removeItem("pdfstudio_user");
    }
    setUser(null);
  };

  // ─────────────────────────────
  // 🔄 REFRESH USER
  // ─────────────────────────────
  const refreshUser = () => {
    if (LOCAL_MODE) {
      const saved = localStorage.getItem("pdfstudio_local_user");
      if (saved) setUser(JSON.parse(saved));
    } else {
      const saved = localStorage.getItem("pdfstudio_user");
      if (saved) setUser(JSON.parse(saved));
    }
  };

  // ─────────────────────────────
  // 📋 ADMIN USER REGISTRY
  // ─────────────────────────────
  const syncToRegistry = (userData) => {
    try {
      const raw = localStorage.getItem("pdfstudio_users_registry");
      const registry = raw ? JSON.parse(raw) : [];
      const idx = registry.findIndex((u) => u.email === userData.email);
      const entry = {
        ...userData,
        last_seen: new Date().toISOString(),
      };
      if (idx >= 0) {
        registry[idx] = { ...registry[idx], ...entry };
      } else {
        registry.push({ ...entry, created_at: new Date().toISOString() });
      }
      localStorage.setItem("pdfstudio_users_registry", JSON.stringify(registry));
    } catch {}
  };

  // ─────────────────────────────
  // ✏️ UPDATE USER
  // ─────────────────────────────
  const updateUser = async (updates) => {
    const updated = { ...user, ...updates };
    setUser(updated);
    const key = LOCAL_MODE ? "pdfstudio_local_user" : "pdfstudio_user";
    localStorage.setItem(key, JSON.stringify(updated));
    syncToRegistry(updated);

    // Persist name change to backend
    if (!LOCAL_MODE && updates.name && user?.email) {
      try {
        await fetch("/api/update_profile.php", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ email: user.email, name: updates.name }),
        });
      } catch {}
    }
    return updated;
  };

  // ─────────────────────────────
  // ⭐ UPGRADE TO PRO
  // ─────────────────────────────
  const upgradeUserToPro = async (utrRef) => {
    if (!LOCAL_MODE && user?.email) {
      const res = await fetch("/api/upgrade_to_pro.php", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: user.email, utr_ref: utrRef }),
      });
      const text = await res.text();
      let backendData = {};
      try { backendData = text ? JSON.parse(text) : {}; } catch {}
      if (!res.ok || backendData.error) {
        throw new Error(backendData.error || "Upgrade failed");
      }
      // Use server-side pro_end_date if available
      const now = new Date();
      return await updateUser({
        plan: "pro",
        pro_start_date: now.toISOString(),
        pro_end_date: backendData.pro_end_date
          ? new Date(backendData.pro_end_date).toISOString()
          : new Date(now.getTime() + 30 * 24 * 60 * 60 * 1000).toISOString(),
        payment_date: now.toISOString(),
        payment_amount: 10,
        utr_ref: utrRef,
        access_status: "active",
      });
    }

    // LOCAL_MODE fallback
    const now = new Date();
    return await updateUser({
      plan: "pro",
      pro_start_date: now.toISOString(),
      pro_end_date: new Date(now.getTime() + 30 * 24 * 60 * 60 * 1000).toISOString(),
      payment_date: now.toISOString(),
      payment_amount: 10,
      utr_ref: utrRef,
      access_status: "active",
    });
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        loading,
        login,
        signup,
        logout,
        refreshUser,
        updateUser,
        upgradeUserToPro,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  return useContext(AuthContext);
}
